import type { ReactNode } from 'react';
import { memo, useLayoutEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AdminBreadcrumb } from 'components/mui/common/Breadcrumbs/AdminBreadcrumb';
import { isMobileApp } from 'utils/common';
import { uiStateActions } from 'store/entities/ui';
import { Container, BreadcrumbsRow, HeaderRow, Title, StyledBreadcrumbs } from './AdminHeaderContainer.styles';

interface Props {
  title: string;
  actions?: ReactNode;
  dataTestId?: string;
  sticky?: boolean;
  openDrawer?: boolean;
}

export const ADMIN_HEADER_HEIGHT_ID = 'admin-header';

const AdminHeaderContainerBase = ({ title, actions, dataTestId, sticky, openDrawer }: Props) => {
  const mobileApp = isMobileApp();
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    if (!sticky || !ref.current) return;
    const el = ref.current;
    const updateHeight = () => dispatch(uiStateActions.setComponentHeight({ id: ADMIN_HEADER_HEIGHT_ID, height: el.getBoundingClientRect().height }));

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      dispatch(uiStateActions.removeComponentHeight(ADMIN_HEADER_HEIGHT_ID));
    };
  }, [dispatch, sticky]);

  return (
    <Container ref={ref} data-testid={dataTestId} sticky={sticky} openDrawer={openDrawer}>
      {!mobileApp && (
        <BreadcrumbsRow>
          <StyledBreadcrumbs aria-label="breadcrumb">
            <AdminBreadcrumb />
          </StyledBreadcrumbs>
        </BreadcrumbsRow>
      )}
      <HeaderRow>
        <Title variant="h1">{title}</Title>
        {actions}
      </HeaderRow>
    </Container>
  );
};

export const AdminHeaderContainer = memo(AdminHeaderContainerBase);
